import { AbstractControl, FormGroup, Validators } from '@angular/forms';

export type Nacionalidade = 'brasileiro' | 'estrangeiro';
export type LocalEndereco = 'brasil' | 'exterior';

export const PAIS_PADRAO = 'Brasil';

/** Respostas do cliente: a nacionalidade decide o documento; o local decide o formato do endereço. */
export interface EscolhaCobranca {
  nacionalidade: Nacionalidade;
  localEndereco: LocalEndereco;
}

/** Nomes dos controles de cada formulário que dependem das escolhas. */
export interface CamposNacionalidade {
  documento: string;
  pais: string;
  cep: string;
  estado: string;
  telefone: string;
  bairro: string;
  numero: string;
}

/**
 * Aplica ao formulário as regras das escolhas de cobrança, cada eixo atuando só nos seus campos.
 * Quando `anterior` é informado, limpa apenas os campos incompatíveis com a mudança ocorrida.
 */
export function aplicarCobranca(
  form: FormGroup,
  escolha: EscolhaCobranca,
  campos: CamposNacionalidade,
  anterior?: EscolhaCobranca,
): void {
  const mudouNacionalidade = anterior !== undefined && anterior.nacionalidade !== escolha.nacionalidade;
  const mudouLocal = anterior !== undefined && anterior.localEndereco !== escolha.localEndereco;

  aplicarDocumento(form, escolha.nacionalidade, campos, mudouNacionalidade);
  aplicarEndereco(form, escolha.localEndereco, campos, mudouLocal);

  for (const nome of Object.values(campos)) {
    const c = controle(form, nome);
    c.markAsUntouched();
    c.updateValueAndValidity();
  }
}

function aplicarDocumento(
  form: FormGroup,
  nacionalidade: Nacionalidade,
  campos: CamposNacionalidade,
  mudou: boolean,
): void {
  const estrangeiro = nacionalidade === 'estrangeiro';
  const documento = controle(form, campos.documento);
  // Estrangeiro nunca informa documento; ao trocar de nacionalidade o valor é descartado.
  if (estrangeiro || mudou) {
    documento.setValue('');
  }
  documento.setValidators(estrangeiro ? [] : [Validators.required]);
}

function aplicarEndereco(
  form: FormGroup,
  local: LocalEndereco,
  campos: CamposNacionalidade,
  mudou: boolean,
): void {
  const exterior = local === 'exterior';
  const pais = controle(form, campos.pais);

  if (exterior) {
    pais.setValidators([Validators.required]);
    if (mudou || pais.value === PAIS_PADRAO) {
      pais.setValue('');
    }
  } else {
    pais.clearValidators();
    pais.setValue(PAIS_PADRAO);
  }

  const dependentes: Array<keyof CamposNacionalidade> = ['cep', 'estado', 'bairro', 'numero'];
  for (const chave of dependentes) {
    controle(form, campos[chave]).setValidators(exterior ? [] : [Validators.required]);
  }
  // Telefone é obrigatório nos dois formatos.
  controle(form, campos.telefone).setValidators([Validators.required]);

  // Voltar ao Brasil: valores livres do exterior podem não caber no formato brasileiro.
  if (!exterior && mudou) {
    controle(form, campos.cep).setValue('');
    controle(form, campos.estado).setValue('');
    controle(form, campos.telefone).setValue('');
  }
}

function controle(form: FormGroup, nome: string): AbstractControl {
  const c = form.get(nome);
  if (!c) {
    throw new Error(`Controle '${nome}' não existe no formulário.`);
  }
  return c;
}
